import React, { useState, useEffect } from 'react';
import { Role, Industry } from '../../../types';
import { apiService } from '../../../services';
import styles from './RoleSelector.module.css';

interface RoleSelectorProps {
  industry: Industry;
  onRoleSelect: (role: Role) => void;
  onBack: () => void;
  selectedRole?: Role | null;
}

const RoleSelector: React.FC<RoleSelectorProps> = ({
  industry,
  onRoleSelect,
  selectedRole
}) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (industry?.id) {
      loadRoles(industry.id);
    }
  }, [industry]);

  const loadRoles = async (industryId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getRolesByIndustry(industryId);
      setRoles(data);
    } catch (err) {
      setError('Error al cargar los roles. Por favor intenta de nuevo.');
      console.error('Error loading roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleClick = (role: Role) => {
    onRoleSelect(role);
  };

  const getRoleIcon = (roleName: string) => {
    const iconMap: { [key: string]: string } = {
      Manager: 'fas fa-user-tie',
      Supervisor: 'fas fa-users',
      Analyst: 'fas fa-chart-line'
    };
    return iconMap[roleName] || 'fas fa-user';
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Cargando roles para {industry.name}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={() => loadRoles(industry.id)} className={styles.retryButton}>
          Intentar de Nuevo
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Selecciona tu Rol</h2>
        <p>Elige el rol que mejor describe tu posición en la industria de {industry.name}</p>
      </div>

      <div className={styles.rolesList}>
        {roles.map((role) => (
          <div
            key={role.id}
            className={`${styles.roleCard} ${
              selectedRole?.id === role.id ? styles.selected : ''
            }`}
            onClick={() => handleRoleClick(role)}
            style={{
              '--theme-color': industry.themeColor || '#3B82F6'
            } as React.CSSProperties}
          >
            <div className={styles.roleIcon}>
              <i className={getRoleIcon(role.name)}></i>
            </div>
            <div className={styles.roleContent}>
              <h3 className={styles.roleName}>{role.name}</h3>
              <p className={styles.roleDescription}>{role.description}</p>
            </div>
            {selectedRole?.id === role.id && (
              <div className={styles.selectedIndicator}>
                <i className="fas fa-check"></i>
              </div>
            )}
          </div>
        ))}
      </div>

      {roles.length === 0 && (
        <div className={styles.noRoles}>
          <p>No hay roles disponibles para {industry.name}</p>
        </div>
      )}
    </div>
  );
};

export default RoleSelector;
